import { HttpClient } from "@angular/common/http";
import { inject, Injectable, signal } from "@angular/core";
import { jwtDecode, JwtPayload } from "jwt-decode";
import { map, Observable, tap } from "rxjs";
import { environment } from "../../../environments/environment";
import {
    TeamMemberAddRequest,
    TeamMemberEditRequest,
} from "../../features/branch-management/branch-management-details/branch-management-details.interface";
import { FirstTimeLogin } from "../models/login.model";
import { UserRole } from "../models/user-role.enum";

export interface User {
    id: string;
    name: string;
    role: UserRole;
    partnerId: string;
    branchName: string;
    branchId: string;
}

interface DecodedToken extends JwtPayload {
    Id: string;
    Role: UserRole;
    Name: string;
    PartnerId: string;
    BranchName: string;
    BranchId: string;
}

export interface PartnerInfo {
    partnerId: string;
    portalUrl: string;
    themeColor: string;
    logoUrl: string;
    bannerUrl: string;
    status: string;
}

@Injectable({
    providedIn: "root",
})
export class PartnerUserService {
    private baseUrl = `${environment.url}/PartnerUser`;
    private http = inject(HttpClient);
    private _currentUser = signal<User | null>(this.getUserInfo());
    readonly currentUser = this._currentUser.asReadonly();
    private _partnerInfo = signal<PartnerInfo | null>(this.getStoredPartnerInfo());
    readonly partnerInfo = this._partnerInfo.asReadonly();

    firstTimeLogin$(payload: FirstTimeLogin): Observable<boolean> {
        return this.http.post<boolean>(`${this.baseUrl}/firstTimeLogin`, payload).pipe(map((_) => true));
    }

    login$(mobileNo: string, password: string, partner: string): Observable<string> {
        return this.http
            .post(
                `${this.baseUrl}/login`,
                {
                    mobileNo,
                    password,
                    urlSlug: partner,
                },
                { responseType: "text" },
            )
            .pipe(
                tap((accessToken: string) => {
                    const decodedToken = jwtDecode<DecodedToken>(accessToken);
                    this._currentUser.set({
                        id: decodedToken.Id,
                        name: decodedToken.Name,
                        role: decodedToken.Role,
                        partnerId: decodedToken.PartnerId,
                        branchName: decodedToken.BranchName,
                        branchId: decodedToken.BranchId,
                    });
                    localStorage.setItem("accessToken", accessToken);
                    return accessToken;
                }),
            );
    }

    getPartnerInfoByUrlSlug$(urlSlug: string): Observable<PartnerInfo> {
        return this.http.get<PartnerInfo>(`${this.baseUrl}/getPartnerInfoByUrlSlug?urlSlug=${urlSlug}`).pipe(
            tap((partnerInfo) => {
                if (partnerInfo.partnerId === "00000000-0000-0000-0000-000000000000") {
                    this._partnerInfo.set(null);
                    localStorage.removeItem("partnerInfo");
                } else {
                    this._partnerInfo.set(partnerInfo);
                    localStorage.setItem("partnerInfo", JSON.stringify(partnerInfo));
                }
            }),
        );
    }

    add$(id: string, payload: TeamMemberAddRequest) {
        return this.http.post(`${this.baseUrl}/add/${id}`, payload);
    }

    update$(id: string, payload: TeamMemberEditRequest) {
        return this.http.post(`${this.baseUrl}/update/${id}`, payload);
    }

    logout(): void {
        this._currentUser.set(null);
        localStorage.removeItem("accessToken");
    }

    private getUserInfo(): User | null {
        const accessToken = localStorage.getItem("accessToken");
        if (!accessToken) return null;

        try {
            const decodedToken = jwtDecode<DecodedToken>(accessToken);
            if (!decodedToken) return null;

            return {
                id: decodedToken.Id,
                name: decodedToken.Name,
                role: decodedToken.Role,
                partnerId: decodedToken.PartnerId,
                branchName: decodedToken.BranchName,
                branchId: decodedToken.BranchId,
            };
        } catch (error) {
            return null;
        }
    }

    private getStoredPartnerInfo(): PartnerInfo | null {
        const storedPartnerInfo = localStorage.getItem("partnerInfo");
        return storedPartnerInfo ? JSON.parse(storedPartnerInfo) : null;
    }
}
